import React, { useRef, useState } from "react";
/** Components */
import { Toasts } from "view/components/Toasts";
/** Services */
import apiLibrary from "services/api";
/** Assets */
import defaultBackGroundImage from "assets/images/defaultAppBackgroundImage.png";
import { ArrowLeftIcon } from "assets/icons/HeroIcons";
import { CameraIcon } from "assets/icons/HeroIcons";
import { setLoadingProgress } from "store/loadingBar";
import { useDispatch } from "react-redux";
import { getRandomProgress } from "utils/getRandomProgress";

interface IProps {
  setFieldValue?: any;
  values?: any;
  editMode?: boolean;
}

export const ObserverOfflineChecklist: React.FC<IProps> = ({
  setFieldValue,
  values,
  editMode,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  /** Image upload function */
  const handleImageUpload = async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      try {
        /** Validate image */
        const imageValidation = ["image/jpg", "image/jpeg", "image/png"];
        if (!imageValidation.includes(file.type)) {
          event.target.value = "";
          return Toasts.error(
            "Please select a valid image file (jpg/png/jpeg)"
          );
        }

        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 25) {
          return Toasts.error("File size must be less than 25 mb!");
        }
        /** Validate image dimensions */
        const dimensionsValid = await new Promise<boolean>((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (img.width === 1080 && img.height === 1920) {
              resolve(true);
            } else {
              Toasts.error("Image dimension must be exactly 1080 x 1920 px");
              resolve(false);
            }
          };
          img.onerror = () => {
            Toasts.error("Failed to read image dimensions");
            resolve(false);
          };
          img.src = URL.createObjectURL(file);
        });

        if (!dimensionsValid) {
          event.target.value = "";
          return;
        }
        dispatch(setLoadingProgress(getRandomProgress()));
        /** Upload file */
        const uploadResponse = await apiLibrary.file.fileUpload(
          file,
          false,
          "public"
        );
        setFieldValue("observerOfflineChecklistImageId", uploadResponse.data);
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message ?? error.message;
        Toasts.error(errorMsg);
      } finally {
        if(fileInputRef.current){
          fileInputRef.current.value = "";
        }
        dispatch(setLoadingProgress(100));
      }
    }
  };

  /** function that trigger onclick */
  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  /** Function to set background color to default */
  const resetImage = async () => {
    try {
      dispatch(setLoadingProgress(getRandomProgress()));
      await new Promise((resolve) => setTimeout(resolve, 300)); 
      setFieldValue("observerOfflineChecklistImageId", null);
      dispatch(setLoadingProgress(100));
    } catch (error) {
      Toasts.error("Error resetting image.");
    } finally {
      dispatch(setLoadingProgress(100));
    }
  };

  return (
    <div className="w-full h-[342px] col-span-1">
      <div>
        <div>
          <p className="font-semibold text-secondaryMid dark:text-textMain text-base">
            Observer Offline Checklist
          </p>
          <p className="text-sm text-textMidLight dark:text-textMain">
            For the best results on all devices,
            <br /> use an image that’s 1080 x 1920 px.
          </p>
          {editMode && (
            <button
              type="button"
              onClick={resetImage}
              disabled={
                values.observerOfflineChecklistImageId === null ||
                !values.observerOfflineChecklistImageId
              }
              className={`text-primary text-xs font-semibold mt-2  ${values.observerOfflineChecklistImageId === null || !values.observerOfflineChecklistImageId ? " cursor-not-allowed " : "cursor-pointer"} `}
            >
              Reset to default
            </button>
          )}
        </div>
        <div
          className={`relative w-[140px] bg-primary_9 h-[285px] rounded-lg flex flex-col  ${editMode ? "mt-0" : "mt-4"}`}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center h-[285px] rounded-lg z-10"
            style={{
              backgroundImage: `url(${values.observerOfflineChecklistImageId?.file ? `"${values.observerOfflineChecklistImageId.file.original}"` : defaultBackGroundImage})`,
            }}
          ></div>
          <div className="px-1 py-1 flex justify-between items-center text-white text-sm relative z-10">
            <span className="text-[7.52px] text-textWhite">9:30</span>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            {editMode && (
              <button
                type="button"
                onClick={triggerImageUpload}
                className="h-8 w-8 disabled:cursor-not-allowed flex justify-center items-center rounded-full bg-bgWhite dark:bg-secondaryLight  absolute right-[-10px] top-[-12px]"
              >
                <CameraIcon className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="flex items-center w-full relative px-2 z-10 gap-1">
            <span>
              <ArrowLeftIcon className="w-2 h-2" fill="#FFFFFF" />
            </span>{" "}
            <p className="text-textWhite text-[6px]">Home screen</p>
          </div>

          <div className="relative z-20 top-[50px] px-1">
            <p className="text-textWhite text-[9.5px] py-2 font-semibold">
              Offline Checklist
            </p>
            <div className="bg-bgGreyish_76 dark:bg-bgMain h-[105px] flex flex-col justify-evenly p-1 rounded-md ">
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-md"></div>
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-md"></div>
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-md"></div>
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-md"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
